import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation } from '@tanstack/react-query';
import { useNotifications } from '@toolpad/core';
import {
    FormContainer,
    SelectElement,
    TextFieldElement,
    useForm,
    useWatch,
} from 'react-hook-form-mui';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
} from '@mui/material';

import { BusinessInput } from '@barboxx/admin-components';
import { CREATE_CATEGORY_SCHEMA } from '@barboxx/admin-utils';
import Api from '@barboxx/api';
import { Category } from '@barboxx/models';
import { CATEGORY_TYPES } from '@barboxx/types';
import { enumToFormOptions } from '@barboxx/utils';

interface Props {
    open: boolean;
    onClose: () => void;
    onSuccess: () => void;
}

export const CreateCategoryDialog = ({ onClose, onSuccess, open }: Props) => {
    const notifications = useNotifications();

    const formContext = useForm<Category.CreateRequest>({
        resolver: yupResolver(CREATE_CATEGORY_SCHEMA),
        defaultValues: {
            businessId: null, // Assuming this is optional for general categories
        },
    });

    const mutateCreateCategory = useMutation({
        mutationKey: ['category'],
        mutationFn: (data: Category.CreateRequest) =>
            Api.Categories.Category.postCategory(data),
        onSuccess: () => {
            formContext.reset();
        },
    });

    const onSubmit = async (values: Category.CreateRequest) => {
        return mutateCreateCategory
            .mutateAsync(values)
            .then(({ data }) => {
                notifications.show(`Created ${values.name}!`, {
                    severity: 'success',
                });
                onClose();
                onSuccess();
            })
            .catch(e => {
                notifications.show('Error creating category!', {
                    severity: 'error',
                });
            });
    };

    const handleCancelCreate = () => {
        formContext.reset();
        onClose();
    };

    const values = useWatch(formContext) as Category;

    return (
        <Dialog
            open={open}
            onClose={onClose}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            fullWidth
        >
            <DialogTitle id="alert-dialog-title">Create Category</DialogTitle>
            <FormContainer onSuccess={onSubmit} formContext={formContext}>
                <DialogContent
                    sx={{ gap: 2, display: 'flex', flexDirection: 'column' }}
                >
                    <TextFieldElement label="Name" name="name" fullWidth />
                    <TextFieldElement
                        label="Description"
                        name="description"
                        fullWidth
                    />
                    <SelectElement
                        label="Category Type"
                        name="categoryType"
                        options={enumToFormOptions(CATEGORY_TYPES)}
                        fullWidth
                        onChange={value => {
                            if (value !== CATEGORY_TYPES.Business) {
                                // Reset businessId when categoryType changes
                                formContext.setValue('businessId', null);
                            }
                        }}
                    />
                    {values.categoryType === CATEGORY_TYPES.Business && (
                        <BusinessInput />
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancelCreate} color="inherit">
                        Cancel
                    </Button>

                    <Button
                        color="primary"
                        variant="contained"
                        loading={formContext.formState.isSubmitting}
                        type="submit"
                        disabled={formContext.formState.isSubmitting}
                    >
                        Create
                    </Button>
                </DialogActions>
            </FormContainer>
        </Dialog>
    );
};
